import React from 'react';
import { useStaffAccess } from '@/hooks/useStaffAccess';
import { useMobileTableLabels } from '@/hooks/useMobileTableLabels';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';
import ParentNotificationBell from '@/components/ParentNotificationBell';
import TrialBanner from '@/components/TrialBanner';
import { 
  Baby, LayoutDashboard, Users, UserCheck, Calendar, 
  Shield, DollarSign, MessageSquare, FileText, LogOut, LogIn, Menu, X, Settings, Clock, Calculator, AlertTriangle, QrCode, HelpCircle, BookOpen, UtensilsCrossed, Upload, Newspaper, Flame, GraduationCap, Truck
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import API from '@/config/api';

const DashboardLayout = ({ children, title }) => {
  const { user, logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  useMobileTableLabels();
  const [centerInfo, setCenterInfo] = React.useState({ center_name: '', logo_url: null });

  React.useEffect(() => {
    if (user) {
      axios.get(`${API}/settings/center`, getAuthHeaders())
        .then(res => setCenterInfo({ center_name: res.data.center_name, logo_url: res.data.logo_url }))
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const staffAccess = useStaffAccess();
  const teacherPages = user?.role === 'provider' ? staffAccess?.pages : null;

  const navItems = [
    { href: '/driver', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Driver App', roles: ['driver'] },
    { 
      href: user?.role === 'admin' ? '/admin' : user?.role === 'provider' ? '/provider' : '/parent', 
      icon: <LayoutDashboard className="w-5 h-5" />, 
      label: 'Dashboard',
      roles: ['admin', 'provider', 'parent']
    },
    { 
      href: '/parent/checkin', 
      icon: <LogIn className="w-5 h-5" />, 
      label: 'Sign In / Out',
      roles: ['parent']
    },
    { 
      href: '/children', 
      icon: <Baby className="w-5 h-5" />, 
      label: 'Children',
      roles: ['admin', 'provider']  // Removed 'parent' - they see their children on dashboard only
    },
    { 
      href: '/attendance', 
      icon: <Calendar className="w-5 h-5" />, 
      label: 'Attendance',
      roles: ['admin', 'provider']
    },
    { 
      href: '/waitlist', 
      icon: <Clock className="w-5 h-5" />, 
      label: 'Waitlist',
      roles: ['admin']
    },
    { 
      href: '/staff', 
      icon: <UserCheck className="w-5 h-5" />, 
      label: 'Staff',
      roles: ['admin']
    },
    { 
      href: '/parents', 
      icon: <Users className="w-5 h-5" />, 
      label: 'Parents',
      roles: ['admin']
    },
    { 
      href: '/drivers', 
      icon: <Truck className="w-5 h-5" />, 
      label: 'Drivers',
      roles: ['admin']
    },
    { 
      href: '/staff/schedule', 
      icon: <Calendar className="w-5 h-5" />, 
      label: 'Schedule',
      roles: ['admin']
    },
    { 
      href: '/staff/ratio-worksheet', 
      icon: <Calculator className="w-5 h-5" />, 
      label: 'Ratio Worksheet',
      roles: ['admin', 'provider']
    },
    { 
      href: '/compliance', 
      icon: <Shield className="w-5 h-5" />, 
      label: 'Compliance',
      roles: ['admin', 'provider']
    },
    { 
      href: '/safety-drills', 
      icon: <Flame className="w-5 h-5" />, 
      label: 'Safety Drills',
      roles: ['admin', 'provider']
    },
    { 
      href: '/continuing-education', 
      icon: <GraduationCap className="w-5 h-5" />, 
      label: 'Staff Training',
      roles: ['admin', 'provider']
    },
    { 
      href: '/billing', 
      icon: <DollarSign className="w-5 h-5" />, 
      label: 'Billing',
      roles: ['admin', 'provider', 'parent']
    },
    { 
      href: '/accounting', 
      icon: <Calculator className="w-5 h-5" />, 
      label: 'Accounting',
      roles: ['admin', 'provider']
    },
    { 
      href: '/messages', 
      icon: <MessageSquare className="w-5 h-5" />, 
      label: 'Messages',
      roles: ['admin', 'provider', 'parent']
    },
    { 
      href: '/daily-reports', 
      icon: <FileText className="w-5 h-5" />, 
      label: 'Daily Reports',
      roles: ['admin', 'provider', 'parent']
    },
    { 
      href: '/learning', 
      icon: <BookOpen className="w-5 h-5" />, 
      label: 'Learning',
      roles: ['admin', 'provider']
    },
    { 
      href: '/menu', 
      icon: <UtensilsCrossed className="w-5 h-5" />, 
      label: 'Menus',
      roles: ['admin', 'provider', 'parent']
    },
    { 
      href: '/newsletters', 
      icon: <Newspaper className="w-5 h-5" />, 
      label: 'Newsletters',
      roles: ['admin']
    },
    { 
      href: '/incident-reports', 
      icon: <AlertTriangle className="w-5 h-5" />, 
      label: 'Incident Reports',
      roles: ['admin', 'provider']  // Removed 'parent'
    },
    { 
      href: '/timeclock', 
      icon: <QrCode className="w-5 h-5" />, 
      label: 'Time Clock',
      roles: ['admin', 'provider']
    },
    { 
      href: '/my-hours', 
      icon: <Clock className="w-5 h-5" />, 
      label: '↳ My Hours',
      roles: ['admin', 'provider'],
      indent: true
    },
    { 
      href: '/timeclock/admin', 
      icon: <QrCode className="w-5 h-5" />, 
      label: '↳ Admin',
      roles: ['admin'],
      indent: true
    },
    { 
      href: '/settings', 
      icon: <Settings className="w-5 h-5" />, 
      label: 'Settings',
      roles: ['admin']
    },
    { 
      href: '/data-migration', 
      icon: <Upload className="w-5 h-5" />, 
      label: 'Data Migration',
      roles: ['admin']
    },
  ];

  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(user?.role) && (user?.role !== 'provider' || !teacherPages || teacherPages.includes(item.href))
  );

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2">
            <img
              src="/icons/icon-192x192.png?v=4"
              alt="CareKids WI"
              className="w-9 h-9 object-contain"
              data-testid="mobile-header-logo"
            />
            <span className="font-heading font-bold">CareKids WI</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <ParentNotificationBell />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-border transform transition-transform duration-200 lg:translate-x-0 lg:static",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-2 px-6 h-16 border-b border-border">
              {centerInfo.logo_url ? (
                <img 
                  src={centerInfo.logo_url.startsWith('http') ? centerInfo.logo_url : `${API}${centerInfo.logo_url.replace('/api', '')}`}
                  alt={centerInfo.center_name || 'Center Logo'}
                  className="w-10 h-10 rounded-xl object-contain"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                />
              ) : null}
              <div className={`w-10 h-10 bg-white rounded-xl items-center justify-center overflow-hidden ${centerInfo.logo_url ? 'hidden' : 'flex'}`}>
                <img
                  src="/icons/icon-192x192.png?v=4"
                  alt="CareKids WI"
                  className="w-10 h-10 object-contain"
                  data-testid="sidebar-header-logo"
                />
              </div>
              <span className="font-heading font-bold text-xl truncate">{centerInfo.center_name || 'CareKids WI'}</span>
            </div>

            {/* User Info */}
            <div className="px-4 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="font-bold text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-sm truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {filteredNavItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "sidebar-item",
                    location.pathname === item.href && "sidebar-item-active",
                    item.indent && "ml-4 text-sm"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Logout */}
            <div className="p-4 border-t border-border">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-destructive"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 min-h-screen">
          {/* Desktop Header with Notification Bell */}
          <div className="hidden lg:flex items-center justify-end px-8 py-4 border-b border-border bg-white">
            <div className="flex items-center gap-4">
              {/* Help Button - Admin Only */}
              {user?.role === 'admin' && (
                <a 
                  href="mailto:info@carekidswi.com?subject=CareKids WI Support Request"
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
                  data-testid="help-btn"
                >
                  <HelpCircle className="w-4 h-4" />
                  Help
                </a>
              )}
              <NotificationBell />
              <ParentNotificationBell />
              <div className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>
          <div className="p-6 lg:p-8">
            <TrialBanner />
            {title && (
              <h1 className="font-heading font-bold text-2xl lg:text-3xl mb-6" data-testid="page-title">
                {title}
              </h1>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
